import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SortOrder } from '../../shared/models/enums/sort-order.enum';
import { State } from '../../shared/models/state';
import { Lookup } from '../../shared/models/lookup.model';
import { APP_CONFIG, Settings } from '../../config/settings';
import { combine } from '../../shared/utilities/route';
import { ProfessionsAndExpertiseService } from '../../shared/services/professions-and-expertise.service';
import { ProfessionsService } from '../../shared/services/professions.service';
import { StatesService } from '../../shared/services/states.service';
import { Profession, Specialty } from 'src/app/shared/models/lookup-profession.model';
import { CreateJobAlertRule } from './models/create-job-alert-rule';
import { JobAlertRuleDisplay } from './models/job-alert-rule-display.model';
import { EmploymentType } from '../../shared/models/employment-type';
import { EmploymentTypesService } from '../../shared/services/employment-types.service';

@UntilDestroy()
@Injectable()
export class JobAlertRulesService {
    private readonly jobAlertRuleEditSelectedSubject$ = new BehaviorSubject<JobAlertRuleDisplay>(null);

    professions$: Observable<Profession[]>;
    states$: Observable<State[]>;
    employmentTypes$: Observable<Lookup[]>;
    specialties$: Observable<Specialty[]>;
    jobAlertRuleEditSelected$: Observable<JobAlertRuleDisplay> = this.jobAlertRuleEditSelectedSubject$.asObservable();

    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        private readonly _professionsService: ProfessionsService,
        private readonly _statesService: StatesService,
        private readonly _employmentTypeService: EmploymentTypesService,
        private readonly _professionAndExpertiseService: ProfessionsAndExpertiseService,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    load(): void {
        this.professions$ = this._professionsService.getProfessions().pipe(
            map((result: Profession[]): Profession[] => {
                return result.sort((a, b) => a.name.localeCompare(b.name));
            }),
            catchError(() => {
                return of([] as Profession[]);
            })
        );
        this.states$ = this._statesService.getStates().pipe(
            map((result: State[]): State[] => {
                return this.reorderStates(result);
            }),
            catchError(() => {
                return of([] as State[]);
            })
        );
        this.employmentTypes$ = this._employmentTypeService.getEmploymentTypes().pipe(
            map((result: EmploymentType[]): Lookup[] => {
                return result.map((et: EmploymentType): Lookup => {
                    return { id: et.id, name: et.name };
                });
            }),
            catchError(() => {
                return of([] as Lookup[]);
            })
        );
        this.specialties$ = this._professionAndExpertiseService.getSpecialties().pipe(
            map((result: Specialty[]): Specialty[] => {
                return result;
            }),
            catchError(() => {
                return of([] as Specialty[]);
            }),
            untilDestroyed(this)
        );
    }

    private reorderStates(states: State[]): State[] {
        const allStatesId = 80;
        const anyId = 81;
        const anyCompactState = 'Any Compact State';

        return states
            .filter((item) => item.id !== allStatesId && item.id !== anyId)
            .sort((a, b) =>
                a.name === anyCompactState
                    ? SortOrder.SortBySecond
                    : b.name === anyCompactState
                      ? SortOrder.SortByFirst
                      : SortOrder.NoSort
            );
    }

    selectEditJobAlertRule(jobAlertRule: JobAlertRuleDisplay) {
        this.jobAlertRuleEditSelectedSubject$.next(jobAlertRule);
    }

    saveJobAlertRule(addJobAlert: CreateJobAlertRule, userId: number): Observable<void> {
        const url = `${this.coreApiPath}/Recruiter/job-alert-rules`;
        const addJobAlertContent = { ...addJobAlert, userId: userId, isActive: true };
        return this.http.post<void>(url, addJobAlertContent);
    }

    updateJobAlertRule(jobAlertRule: CreateJobAlertRule, ruleId: number, userId: number): Observable<void> {
        const url = `${this.coreApiPath}/Recruiter/job-alert-rules/${ruleId}`;
        const updateJobAlertRuleContent = { ...jobAlertRule, userId: userId, isActive: true };
        return this.http.put<void>(url, updateJobAlertRuleContent);
    }

    loadJobAlertRules(recruiterId: number): Observable<JobAlertRuleDisplay[]> {
        const url = `${this.coreApiPath}/Recruiter/${recruiterId}/job-alert-rules`;
        return this.http.get<JobAlertRuleDisplay[]>(url);
    }

    getJobAlertRule(recruiterId: number, jobAlertName: string): Observable<JobAlertRuleDisplay> {
        const url = `${this.coreApiPath}/Recruiter/${recruiterId}/job-alert-rules/${encodeURIComponent(jobAlertName)}`;
        return this.http.get<JobAlertRuleDisplay>(url);
    }

    deleteJobAlert(jobAlertRuleId: number): Observable<void> {
        const url = `${this.coreApiPath}/Recruiter/${jobAlertRuleId}`;
        return this.http.delete<void>(url);
    }
}
